import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Skeleton from "@mui/material/Skeleton";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import { useTranslation } from "react-i18next";
import {
	useGetCompanyWithIdNotesQuery,
	useUpdateCompanyWithIdNotesMutation,
} from "../../../../features/companies/companiesApiSlice";

function formatDateWithLeadingZeros(date) {
	const day = String(date.getDate()).padStart(2, "0");
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const year = date.getFullYear();

	return `${day}-${month}-${year}`;
}

const Notes = ({ companyId }) => {
	const { t } = useTranslation();
	const { enqueueSnackbar } = useSnackbar();
	const [updateNotes, { isLoading: isUpdatingNotes }] =
		useUpdateCompanyWithIdNotesMutation();
	const { data: notes, isLoading } = useGetCompanyWithIdNotesQuery(companyId);

	const [unsavedNotes, setUnsavedNotes] = useState([]);
	const [notesState, setNotesState] = useState([]);
	const [savingNotes, setSavingNotes] = useState({});

	const handleAppendNote = (e) => {
		e.preventDefault();
		const today = formatDateWithLeadingZeros(new Date());

		const newNote = {
			id: `note-${uuidv4()}`,
			date: today,
			value: "",
		};

		setUnsavedNotes([...unsavedNotes, newNote]);
	};

	const handleNoteChange = (id, newValue) => {
		setUnsavedNotes((prevNotes) =>
			prevNotes.map((note) =>
				note.id === id ? { ...note, value: newValue } : note
			)
		);
	};

	const handleNoteSave = (id) => {
		setSavingNotes((prevSavingNotes) => ({ ...prevSavingNotes, [id]: true }));

		updateNotes({
			companyId,
			note: {
				date: formatDateWithLeadingZeros(new Date()),
				value: unsavedNotes.find((note) => note.id === id).value,
			},
		})
			.unwrap()
			.then((_payload) => {
				setNotesState((prevNotesState) => [
					{
						date: formatDateWithLeadingZeros(new Date()),
						value: unsavedNotes.find((note) => note.id === id).value,
					},
					...prevNotesState,
				]);
				setUnsavedNotes((prevNotes) =>
					prevNotes.filter((note) => note.id !== id)
				);
				setSavingNotes((prevSavingNotes) => {
					const newSavingNotes = { ...prevSavingNotes };
					delete newSavingNotes[id];
					return newSavingNotes;
				});
				enqueueSnackbar(t("notes_updated"), {
					variant: "success",
				});
			})
			.catch((error) => {
				if (error?.data?.errors) {
					const errors = error.data.errors;
					Object.entries(errors).forEach(([key, message]) => {
						enqueueSnackbar(message[0], {
							variant: "error",
							preventDuplicate: true,
						});
					});
				} else {
					enqueueSnackbar(error?.data?.message || t("default_error"), {
						variant: "error",
						preventDuplicate: true,
					});
				}
			});
	};

	useEffect(() => {
		if (notes?.data) {
			setNotesState(notes.data);
		}
	}, [notes]);

	if (isLoading || !notesState) {
		return (
			<Box>
				<Box>
					<Skeleton variant="rectangular" width={113.15} height={35.8} />
				</Box>
				<Box
					sx={{
						mt: 2,
						width: "calc(100% - 30px)",
					}}
				>
					<Skeleton variant="rectangular" height={200} />
				</Box>
			</Box>
		);
	}

	return (
		<Box>
			<Box>
				<Button
					variant="contained"
					onClick={handleAppendNote}
					disabled={isUpdatingNotes}
				>
					{t("new_note")}
				</Button>
			</Box>
			<Box
				sx={{
					mt: 2,
					width: "calc(100% - 30px)",
					maxHeight: "200px",
					height: "210px",
					paddingRight: "30px",
					paddingLeft: "6px",
					py: "8px",
					border: "1px solid #BFBFBF",
					position: "relative",
				}}
			>
				<Box
					sx={{
						position: "absolute",
						top: 0,
						left: 0,
						right: 0,
						bottom: 0,
						overflow: "scroll",
						paddingRight: "37px",
						paddingLeft: "6px",
						py: "8px",
						mr: "-30px",
					}}
				>
					{unsavedNotes.map((note) => {
						return (
							<Box
								key={note.id}
								sx={{ display: "flex", gap: "6px", alignItems: "center" }}
							>
								<Typography sx={{ flex: "0", whiteSpace: "nowrap" }}>
									{note.date}:
								</Typography>
								<TextField
									sx={{ flex: "1" }}
									type="text"
									size="small"
									variant="standard"
									fullWidth
									value={note.value}
									onChange={(e) => handleNoteChange(note.id, e.target.value)}
									slotProps={{
										input: {
											endAdornment: savingNotes[note.id] && (
												<CircularProgress size={20} />
											),
										},
									}}
								/>
								<Button
									variant="contained"
									onClick={() => handleNoteSave(note.id)}
									disabled={isUpdatingNotes}
								>
									{t("save")}
								</Button>
							</Box>
						);
					})}
					{notesState.map((note) => {
						return (
							<Box
								key={`note-d-${note.id}`}
								sx={{ display: "flex", gap: "6px", alignItems: "start" }}
							>
								<Typography sx={{ flex: "0", whiteSpace: "nowrap" }}>
									{note.date}:
								</Typography>
								<Box
									sx={{
										flex: "1",
										display: "flex",
										alignItems: "center",
									}}
								>
									<Typography>{note.value}</Typography>
								</Box>
							</Box>
						);
					})}
				</Box>
			</Box>
		</Box>
	);
};

export default Notes;
